
import { useState } from 'react';

export default function FinalCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    hospital: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formType, setFormType] = useState<'pilot' | 'contact'>('pilot');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    // Capturar el tipo actual en el momento del submit
    const currentFormType = formType;
    console.log('📤 Tipo de formulario actual:', currentFormType);

    try {
      console.log('📤 Enviando formulario:', { ...formData, type: currentFormType });
      
      const response = await fetch('https://us-central1-prebedai-web.cloudfunctions.net/sendEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          type: currentFormType
        })
      });

      const data = await response.json();
      console.log('📥 Respuesta - Status:', response.status, 'Data:', data);

      if (!response.ok) {
        console.error('❌ Error del servidor:', data);
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Error al enviar el formulario. Por favor, inténtalo de nuevo.');
        return;
      }

      console.log('✅ Email enviado correctamente:', data);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', hospital: '', phone: '', message: '' });
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
      
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      setSubmitStatus('error');
      setErrorMessage('Error de conexión. Por favor, verifica tu conexión a internet e inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="final-cta" className="py-16 sm:py-24 bg-gradient-to-br from-[#6D28D9] to-[#8B5CF6] relative overflow-hidden">
      {/* Decorative dots pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
        {/* Title */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
          ¿Listo para optimizar<br className="hidden sm:block" />
          <span className="sm:hidden"> </span>la gestión de camas?
        </h2>

        {/* Subtitle */}
        <p className="text-base sm:text-xl text-white/90 mb-8 sm:mb-12 max-w-3xl mx-auto">
          Solicita un piloto en tu hospital o contáctanos para resolver tus dudas.
        </p>

        {/* Switch Profesional de Tipo de Formulario */}
        <div className="flex justify-center mb-10 sm:mb-12">
          <div className="inline-flex bg-white/15 backdrop-blur-md rounded-full p-1 border border-white/20 shadow-lg">
            <button
              type="button"
              onClick={() => {
                console.log('🔄 Cambiando a tipo: pilot');
                setFormType('pilot');
              }}
              className={`px-8 sm:px-10 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 ${
                formType === 'pilot'
                  ? 'bg-white text-[#6D28D9] shadow-md'
                  : 'text-white/70 hover:text-white/90'
              }`}
            >
              Solicitar Piloto
            </button>
            <button
              type="button"
              onClick={() => {
                console.log('🔄 Cambiando a tipo: contact');
                setFormType('contact');
              }}
              className={`px-8 sm:px-10 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 ${
                formType === 'contact'
                  ? 'bg-white text-[#6D28D9] shadow-md'
                  : 'text-white/70 hover:text-white/90'
              }`}
            >
              Contactar
            </button>
          </div>
        </div>

        {/* Pilot Request Form */}
        <div id="pilot-form" className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-white/20">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 text-center">
            {formType === 'pilot' ? 'Solicitar Piloto' : 'Formulario de Contacto'}
          </h3>
          <p className="text-white/80 text-sm mb-6 text-center">
            {formType === 'pilot' 
              ? 'Implementaremos PreBedAI en tu hospital y te acompañamos en todo el proceso.'
              : 'Cuéntanos tus necesidades y en qué podemos ayudarte.'}
          </p>
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4" data-readdy-form id="pilot-request-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <input
                type="text"
                name="name"
                placeholder="Tu nombre"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="px-4 py-3 bg-white/90 border-2 border-transparent rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-white transition-colors text-base"
              />
              <input
                type="email"
                name="email"
                placeholder="Tu email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="px-4 py-3 bg-white/90 border-2 border-transparent rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-white transition-colors text-base"
              />
            </div>
            <input
              type="text"
              name="hospital"
              placeholder={formType === 'pilot' ? 'Hospital / Centro' : 'Empresa / Organización'}
              value={formData.hospital}
              onChange={(e) => setFormData({ ...formData, hospital: e.target.value })}
              required
              className="w-full px-4 py-3 bg-white/90 border-2 border-transparent rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-white transition-colors text-base"
            />
            <input
              type="tel"
              name="phone"
              placeholder="Teléfono"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="w-full px-4 py-3 bg-white/90 border-2 border-transparent rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-white transition-colors text-base"
            />
            <textarea
              name="message"
              placeholder={formType === 'pilot' ? 'Comentarios (opcional)' : 'Cuéntanos en qué podemos ayudarte'}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={3}
              required={formType === 'contact'}
              className="w-full px-4 py-3 bg-white/90 border-2 border-transparent rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-white transition-colors text-base resize-none"
            ></textarea>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-3 sm:py-4 bg-white text-[#6D28D9] rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm sm:text-base shadow-lg"
            >
              {isSubmitting ? 'Enviando...' : (formType === 'pilot' ? 'Enviar Solicitud' : 'Enviar Mensaje')}
            </button>
          </form>

          {submitStatus === 'success' && (
            <div className="mt-4 p-4 bg-green-500/20 backdrop-blur-sm border border-green-400/50 rounded-lg text-white animate-fade-in">
              <div className="flex items-center justify-center gap-2">
                <i className="ri-checkbox-circle-line text-lg"></i>
                <span className="font-medium">¡Solicitud enviada con éxito! Te contactaremos pronto.</span>
              </div>
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mt-4 p-4 bg-red-500/20 backdrop-blur-sm border border-red-400/50 rounded-lg text-white animate-fade-in">
              <div className="flex flex-col items-center justify-center gap-2">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg"></i>
                  <span className="font-medium">Error al enviar</span>
                </div>
                {errorMessage && (
                  <p className="text-sm text-white/90 text-center">{errorMessage}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Microcopy */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-white/70">
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-white"></i>
            <span>Implementación personalizada</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-white"></i>
            <span>Soporte dedicado</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-white"></i>
            <span>Sin compromiso</span>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-8 text-xs sm:text-sm space-y-2">
          <p className="text-white/60">
            ¿Preguntas? <a href="mailto:info@prebedai.com" className="text-white/90 hover:text-white underline transition-colors">info@prebedai.com</a>
          </p>
          <p className="text-white/60 flex items-center justify-center gap-2">
            <i className="ri-phone-line text-white/90"></i>
            <a href="tel:+34656756397" className="text-white/90 hover:text-white underline transition-colors">+34 656 756 397</a>
          </p>
        </div>
      </div>
    </section>
  );
}
